import { formatMoney, toMinor, type ContentKey } from '@nvd/claim-shared';
import { ClaimedItems } from '../components/ClaimedItems';
import { StepLayout } from '../components/StepLayout';
import { useAction, usePortal } from '../Portal';
import { buildSubmitRequest } from '../state/machine';

type Copy = Record<'title' | 'body' | 'subtotal' | 'tax' | 'shipping' | 'total' | 'submit', ContentKey>;

const copyOf = <P extends 'refund' | 'storeCredit'>(prefix: P) =>
  ({
    title: `${prefix}.title`,
    body: `${prefix}.body`,
    subtotal: `${prefix}.subtotal`,
    tax: `${prefix}.tax`,
    shipping: `${prefix}.shipping`,
    total: `${prefix}.total`,
    submit: `${prefix}.submit`,
  }) as const satisfies Copy;

/** Refund and store credit confirm the same Shopify breakdown; only the wording differs. */
function AmountConfirm({ copy }: { copy: Copy }) {
  const { t, api, state, dispatch } = usePortal();
  const { busy, error, run } = useAction();
  const refund = state.refund;
  const resolution = state.resolution;
  if (!refund || !resolution) return null;

  const rows = [
    { label: t(copy.subtotal), money: refund.subtotal, always: true },
    { label: t(copy.tax), money: refund.tax, always: false },
    { label: t(copy.shipping), money: refund.shipping, always: false },
  ].filter((row) => row.always || toMinor(row.money) !== 0);

  const confirm = () =>
    run(async () => {
      const { outcome } = await api.submit(buildSubmitRequest(state.draft, resolution.id));
      dispatch({ type: 'SUBMITTED', outcome });
    });

  return (
    <StepLayout
      title={t(copy.title)}
      paragraph={t(copy.body)}
      error={error}
      busy={busy}
      onBack={() => dispatch({ type: 'BACK' })}
      onNext={confirm}
      nextLabel={t(copy.submit)}
    >
      <ClaimedItems />
      <dl className="nvd-claim-portal__summary">
        {rows.map((row) => (
          <div key={row.label} className="nvd-claim-portal__summary-row">
            <dt>{row.label}</dt>
            <dd>{formatMoney(row.money)}</dd>
          </div>
        ))}
        <div className="nvd-claim-portal__summary-row nvd-claim-portal__summary-row--total">
          <dt>{t(copy.total)}</dt>
          <dd>{formatMoney(refund.total)}</dd>
        </div>
      </dl>
    </StepLayout>
  );
}

export const RefundConfirm = () => <AmountConfirm copy={copyOf('refund')} />;
export const StoreCreditConfirm = () => <AmountConfirm copy={copyOf('storeCredit')} />;
